// Sesja lekcji kursu v2 (SPEC §21.3, §23.14, §23.26): układa kroki 1–6 jednej lekcji `LessonV2` w listę zadań dla odtwarzacza.
//   1) Listen and repeat — 2 zdania opisu (powtórz po bocie)        → zadania `speak` (repeat), obrazek historii
//   2) Now it’s your turn — 2 zdania wzbogacone (przeczytaj)         → `speak` (read), ten sam obrazek
//   3) Scenka Tom / Emma — dziecko gra swoją postać (chłopiec Toma, dziewczynka Emmę; `withRole`) → `speak` (scene) z kwestią rozmówcy jako „cue”, bez obrazka; potem karta gramatyczna „Zwróć uwagę”, jeśli lekcja ją ma
//   4) Popatrz i wybierz — każdy cel RAZ: obrazek słowa (albo kafelek) + pytanie + 4 słowa, po odpowiedzi słowo, znaczenie i dźwięk → `quiz`
//   5) Brakujące słowa — każdy cel RAZ: luka w zdaniu z lekcji, bez obrazka → `cloze`
//   6) Zakończenie — losowo na zmianę „Zbuduj zdanie” (+ powiedz je) i „Co powiesz?”, bez obrazków → `build` + `speak` / `say`
// Lista jest deterministyczna dla danego ziarna (`seed`, np. „id lekcji|data”): to samo ziarno = te same zadania i te same id, więc wznowienie po odświeżeniu
// strony (src/lib/runSnapshot.ts) działa, a następnego dnia kolejność ćwiczeń jest inna. Czysty kod, bez DOM i sieci.

import { config as defaultConfig } from '../config.ts';
import type { Config } from '../config.ts';
import type { AnchorTile, BanterLine, BuildTask, ClozeTask, ImageRef, QuizTask, SayCard, SayTask, SceneCue, SpeakTask, Task } from '../types.ts';
import { audioIds } from '../speech/audioIds.ts';
import { pickBanter } from './banter.ts';
import { forGender } from './gender.ts';
import type { Gender } from './gender.ts';
import { grammarCard } from './grammarNotes.ts';
import type { GrammarNotes } from './grammarNotes.ts';
import type { LessonV2 } from './levels.ts';
import { deriveSteps, shuffleOptions, shuffleWith, withRole } from './lessonSteps.ts';
import type { BuildStep, ClozeStep, LessonSteps, QuizStep, SayStep, SceneLine, StepAnchor } from './lessonSteps.ts';
import { reviewKind } from './review.ts';
import { mulberry32, shuffle, shuffleDifferent } from './rng.ts';

export interface CourseSessionInput {
  lesson: LessonV2;
  banter: readonly BanterLine[];
  /** Notatki gramatyczne (karta „Zwróć uwagę”); brak = lekcja bez karty. */
  notes?: GrammarNotes;
  gender?: Gender;
  /** Ziarno losowania: to samo ziarno = ta sama lista zadań (np. `${lesson.id}|${data}`). */
  seed: string;
  /** Poziom podpowiedzi „Co powiesz?”: 1 = karty ze zdaniami (domyślnie), 2 = same głośniki, 3 = sama sytuacja (SPEC §23.13.3). */
  sayLevel?: 1 | 2 | 3;
  cfg?: Config;
}

/** Ziarno liczbowe z napisu (FNV-1a). */
export function hashSeed(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

export type FinalItem<S, B> = { kind: 'say'; step: S } | { kind: 'build'; step: B };

/**
 * Krok 6: `slots` zadań, w każdym losowo „Co powiesz?” albo „Zbuduj zdanie”, w losowej kolejności. Gdy dostępne są oba rodzaje, wypadają oba
 * (jak w kroku 5, gdzie luka i pytanie do obrazka też się mieszają); gdy jednego brak, całość jest z drugiego. Kart „Co powiesz?” jest tyle, ile ma lekcja (maks. 2).
 */
export function mixFinal<S, B>(says: readonly S[], builds: readonly B[], slots: number, rng: () => number): Array<FinalItem<S, B>> {
  const total = Math.min(slots, says.length + builds.length);
  if (total <= 0) return [];
  let kSay: number;
  if (says.length === 0) kSay = 0;
  else if (builds.length === 0) kSay = total;
  else if (total === 1) kSay = rng() < 0.5 ? 1 : 0;
  else {
    const lo = Math.max(1, total - builds.length);
    const hi = Math.min(says.length, total - 1);
    kSay = lo >= hi ? lo : lo + Math.floor(rng() * (hi - lo + 1));
  }
  const pick = <T>(list: readonly T[], n: number): T[] => {
    const idx = list.map((_, i) => i);
    for (let i = idx.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [idx[i], idx[j]] = [idx[j]!, idx[i]!];
    }
    return idx.slice(0, n).sort((a, b) => a - b).map((i) => list[i]!);
  };
  const items: Array<FinalItem<S, B>> = [...pick(says, kSay).map((step): FinalItem<S, B> => ({ kind: 'say', step })), ...pick(builds, total - kSay).map((step): FinalItem<S, B> => ({ kind: 'build', step }))];
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [items[i], items[j]] = [items[j]!, items[i]!];
  }
  return items;
}

/** Obrazek historii lekcji (kroki 1–2): id pliku i emoji celów jako zapas, dopóki obrazek nie został wygenerowany. */
export function imageRefOf(lesson: LessonV2, id: string): ImageRef {
  return { id, emoji: lesson.targets.map((t) => t.emoji).filter((e): e is string => Boolean(e)) };
}

/** Kotwica słowa jako pola zadania: obrazek słowa (z emoji słowa jako zapasem) albo kafelek (SPEC §23.24, §23.26). */
function anchorFields(a: StepAnchor, emoji: string | undefined): { image: ImageRef } | { tile: AnchorTile } {
  return a.kind === 'image' ? { image: { id: a.image, emoji: emoji ? [emoji] : [] } } : { tile: { emoji: a.emoji, situationPl: a.situationPl } };
}

const cueOf = (l: SceneLine): SceneCue => ({ who: l.who, text: l.text, pl: l.pl, audioId: audioIds.lesson(l.id), slowAudioId: audioIds.lessonSlow(l.id), voice: l.voice });

/** Pytanie „Popatrz i wybierz” (krok 4 lekcji i powtórka dla pudełek 1–2): obrazek albo kafelek celu, pytanie, cztery słowa w losowej kolejności. */
function quizTaskOf(id: string, q: QuizStep, rng: () => number): QuizTask {
  const { options, correct } = shuffleOptions(q, rng);
  return { kind: 'quiz', id, word: q.word, wordPl: q.wordPl, q: q.q, pl: q.pl, options, correct, audioId: audioIds.lesson(q.audioFrom), slowAudioId: audioIds.lessonSlow(q.audioFrom), ...anchorFields(q.anchor, q.emoji) };
}

/** Luka (krok 5 lekcji i powtórka dla pudełka 3 oraz dla słów bez karty „Co powiesz?”); `review: true` = powtórka (etykieta „Powtórka”). */
function clozeTaskOf(id: string, c: ClozeStep, lesson: LessonV2, review: boolean): ClozeTask {
  const t: ClozeTask = {
    kind: 'cloze',
    id,
    word: { word: c.word, pl: c.pl, sentenceId: c.audioFrom },
    sentence: c.text,
    pl: c.textPl,
    review,
    answer: c.answer,
    audioId: audioIds.lesson(c.audioFrom),
    ...(c.cue ? { cue: cueOf(c.cue) } : {}),
    ...(c.who ? { who: c.who, me: c.who === lesson.scene.learner } : {}),
  };
  const emoji = lesson.targets.find((x) => x.word === c.word)?.emoji;
  if (emoji) t.word.emoji = emoji;
  return t;
}

/** „Co powiesz?” (krok 6 lekcji i powtórka dla pudełek 4–5, gdy słowo ma kartę). */
function sayTaskOf(id: string, s: SayStep, level: 1 | 2 | 3, seedNum: number): SayTask {
  const cards: SayCard[] = s.options.map((o) => ({ text: o.text, pl: o.pl, who: o.who, voice: o.voice, audioId: audioIds.lesson(o.id), slowAudioId: audioIds.lessonSlow(o.id), correct: o.correct, ...(o.whyPl ? { whyPl: o.whyPl } : {}) }));
  const partner = { who: s.partner.who, text: s.partner.text, pl: s.partner.pl, voice: s.partner.voice, audioId: audioIds.lesson(s.partner.id), slowAudioId: audioIds.lessonSlow(s.partner.id) };
  return { kind: 'say', id, word: s.word, situationPl: s.situationPl, partner, cards: shuffle(cards, seedNum + s.id.length), accept: s.accept, level };
}

/**
 * Wstęp do scenki mówi, kim jest dziecko („You are Emma!”). Bierzemy linijkę z imieniem jego roli albo bez imienia; gdy takiej w treści nie ma (np. serwer ma tylko wersję z Emmą,
 * a dziecko gra Toma), przerabiamy imię w istniejącej — z własnym id (`i-scene.tom`), więc nagranie `b.i-scene.tom` powstaje z `planAudio`/generatora albo mówi głos zapasowy.
 */
export function sceneIntro(lines: readonly BanterLine[], role: string, seed: number): BanterLine | null {
  const pool = lines.filter((l) => l.category === 'intro' && l.step === 'scene');
  if (pool.length === 0) return null;
  const hasName = (l: BanterLine, name: RegExp): boolean => name.test(l.text);
  const anyName = /\b(?:Tom|Emma)\b/;
  const mine = new RegExp(`\\b${role.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`);
  // Linijka mówi, kim jest dziecko, zwrotem „You are <imię>” — wtedy liczy się tylko to imię („Tom starts the scene. You are Emma!” jest dobra dla Emmy, nie dla Toma).
  // Bez takiego zwrotu: dobra jest linijka bez żadnego imienia postaci albo z samym imieniem roli.
  const youAre = /\b(?:You are|Ty jesteś) (Tom|Emma)\b/;
  const others = new RegExp(`\\b(?:${['Tom', 'Emma'].filter((n) => n !== role).join('|')})\\b`);
  const ok = pool.filter((l) => {
    const m = youAre.exec(l.text);
    return m ? m[1] === role : (hasName(l, mine) && !others.test(l.text)) || !hasName(l, anyName);
  });
  if (ok.length > 0) return ok[Math.abs(Math.trunc(seed)) % ok.length]!;
  const base = pool[Math.abs(Math.trunc(seed)) % pool.length]!;
  // podmieniamy imię po „You are” / „Ty jesteś”; gdy takiego zwrotu nie ma, każde imię postaci
  const swap = (t: string): string => (/\b(?:You are|Ty jesteś) (?:Tom|Emma)\b/.test(t) ? t.replace(/\b(You are|Ty jesteś) (?:Tom|Emma)\b/g, `$1 ${role}`) : t.replace(/\b(?:Tom|Emma)\b/g, role));
  return { ...base, id: `${base.id}.${role.toLowerCase()}`, text: swap(base.text), ...(base.pl ? { pl: swap(base.pl) } : {}) };
}

function intro(lines: readonly BanterLine[], step: NonNullable<BanterLine['step']>, seed: number, role?: string): Task[] {
  // wstęp do etapu jest ozdobą: gdy treść z serwera nie ma linijki dla tego etapu, lekcja idzie dalej bez niego
  if (!lines.some((l) => l.category === 'intro' && l.step === step)) return [];
  const line = step === 'scene' && role ? sceneIntro(lines, role, seed) : pickBanter(lines, 'intro', seed, step);
  return line ? [{ kind: 'banter', id: `intro:${step}`, line }] : [];
}

export function buildCourseSession(input: CourseSessionInput): Task[] {
  return localizeCourseTasks(buildCourseTasks(input), input.gender ?? '');
}

function buildCourseTasks(input: CourseSessionInput): Task[] {
  const cfg = input.cfg ?? defaultConfig;
  const lesson = withRole(input.lesson, input.gender); // chłopiec gra Toma, dziewczynka Emmę (patch-50)
  const steps = deriveSteps(lesson);
  const seedNum = hashSeed(input.seed);
  const rng = mulberry32(seedNum);
  const tasks: Task[] = [];
  let n = 0;
  const nextId = (kind: string, ref: string): string => `${String(++n).padStart(2, '0')}:${kind}:${ref}`;
  const intros = (step: NonNullable<BanterLine['step']>): void => {
    for (const t of intro(input.banter, step, seedNum, steps.scene.learner)) tasks.push(t.kind === 'banter' ? { ...t, id: nextId('banter', t.line.id) } : t);
  };
  const describeImg = imageRefOf(lesson, lesson.describe.image);

  // 1) Listen and repeat
  intros('repeat');
  for (const s of steps.listen) {
    const t: SpeakTask = { kind: 'speak', id: nextId('speak', s.id), phase: 'repeat', sentenceId: s.id, text: s.text, pl: s.pl, plMode: 'tap', autoPlay: true, audioId: audioIds.lesson(s.id), slowAudioId: audioIds.lessonSlow(s.id), image: describeImg };
    tasks.push(t);
  }

  // 2) Now it's your turn
  intros('read');
  for (const s of steps.yourTurn) {
    const t: SpeakTask = { kind: 'speak', id: nextId('speak', s.id), phase: 'read', sentenceId: s.id, text: s.text, pl: s.pl, plMode: 'tap', autoPlay: false, audioId: audioIds.lesson(s.id), slowAudioId: audioIds.lessonSlow(s.id), image: describeImg };
    tasks.push(t);
  }

  // 3) Scenka (bez obrazka — SPEC §23.26): kwestie drugiej osoby czyta bot (swoim głosem), kwestie dziecka mówi dziecko
  intros('scene');
  for (const turn of steps.scene.turns) {
    const t: SpeakTask = {
      kind: 'speak',
      id: nextId('speak', turn.id),
      phase: 'scene',
      sentenceId: turn.line.id,
      text: turn.line.text,
      pl: turn.line.pl,
      plMode: 'tap',
      autoPlay: false,
      audioId: audioIds.lesson(turn.line.id),
      slowAudioId: audioIds.lessonSlow(turn.line.id),
      role: steps.scene.learner,
      cue: turn.cue.map(cueOf),
    };
    tasks.push(t);
  }
  const card = input.notes ? grammarCard(lesson, input.notes) : null;
  if (card) tasks.push({ kind: 'grammar', id: nextId('grammar', card.id), card });

  // 4) Popatrz i wybierz: każdy cel raz, w losowej kolejności; kotwica celu w polu obrazka, kolejność odpowiedzi też losowa
  intros('words');
  for (const q of shuffleWith(steps.quiz, rng)) tasks.push(quizTaskOf(nextId('quiz', q.word), q, rng));

  // 5) Brakujące słowa: każdy cel raz, w losowej kolejności, bez obrazka
  intros('type');
  for (const c of shuffleWith(steps.cloze, rng)) tasks.push(clozeTaskOf(nextId('cloze', c.word), c, lesson, false));

  // 6) Zakończenie: losowo „Zbuduj zdanie” (i powiedz je) albo „Co powiesz?”
  intros('final');
  const level = input.sayLevel ?? 1;
  for (const item of mixFinal<SayStep, BuildStep>(steps.say, steps.build, cfg.session.buildCount, rng)) {
    if (item.kind === 'build') {
      const b = item.step;
      const build: BuildTask = { kind: 'build', id: nextId('build', b.id), sentenceId: b.id, text: b.text, pl: b.pl, chunks: shuffleDifferent(b.chunks, seedNum + b.id.length), audioId: audioIds.lesson(b.audioFrom), slowAudioId: audioIds.lessonSlow(b.audioFrom) };
      tasks.push(build);
      tasks.push({ kind: 'speak', id: nextId('speak', `${b.id}-build`), phase: 'build', sentenceId: b.id, text: b.text, pl: b.pl, plMode: 'hidden', autoPlay: false, audioId: audioIds.lesson(b.audioFrom), slowAudioId: audioIds.lessonSlow(b.audioFrom) });
    } else {
      tasks.push(sayTaskOf(nextId('say', item.step.id), item.step, level, seedNum));
    }
  }
  return tasks;
}

// --- Powtórka dnia (patch-47, SPEC §23.27) -------------------------------------------------------------------------------------------------------------

/** Lekcja, z której bierzemy ćwiczenie dla słowa: najpierw ukończona (dziecko ją widziało), a gdy słowo jest celem kilku lekcji — wcześniejsza w kursie. */
export function lessonOfWord(word: string, lessons: readonly LessonV2[], done: Readonly<Record<string, unknown>> = {}): LessonV2 | undefined {
  return lessons.find((l) => done[l.id] !== undefined && l.targets.some((t) => t.word === word)) ?? lessons.find((l) => l.targets.some((t) => t.word === word));
}

export interface ReviewSessionInput {
  /** Słowa do powtórzenia z pudełkami (kolejność z planu `planReviews`; sesja miesza je ziarnem). */
  picks: ReadonlyArray<{ word: string; box: 1 | 2 | 3 | 4 | 5 }>;
  lessons: readonly LessonV2[];
  /** Ukończone lekcje kursu (`progress.course.done`) — ćwiczenie bierzemy z lekcji, którą dziecko robiło. */
  done?: Readonly<Record<string, unknown>>;
  gender?: Gender;
  /** Ziarno: to samo ziarno i te same słowa = te same zadania i te same id (wznowienie po odświeżeniu). */
  seed: string;
  sayLevel?: 1 | 2 | 3;
}

/**
 * Lista zadań „Powtórki dnia”: dla każdego słowa jedno ćwiczenie wybrane z pudełka (`reviewKind`, SPEC §23.13.2 pkt 7): 1–2 pytanie do obrazka (kotwica słowa),
 * 3 luka w zdaniu, 4–5 „Co powiesz?” (gdy lekcja ma kartę tego słowa, inaczej luka). Ćwiczenia pochodzą z lekcji, w której słowo jest celem, więc nagrania
 * i obrazki są te same co w lekcji — nic nowego do generowania. Słowo bez lekcji w treści (np. usuniętej) jest pomijane. Czysty kod, bez DOM i sieci.
 */
export function buildReviewSession(input: ReviewSessionInput): Task[] {
  const seedNum = hashSeed(input.seed);
  const rng = mulberry32(seedNum);
  const level = input.sayLevel ?? 1;
  const stepsOf = new Map<string, LessonSteps>();
  const items: Array<{ word: string; lesson: LessonV2; kind: 'quiz' | 'cloze' | 'say'; quiz?: QuizStep; cloze?: ClozeStep; say?: SayStep }> = [];
  for (const p of input.picks) {
    const lesson = lessonOfWord(p.word, input.lessons, input.done);
    if (!lesson) continue;
    let steps = stepsOf.get(lesson.id);
    if (!steps) stepsOf.set(lesson.id, (steps = deriveSteps(withRole(lesson, input.gender))));
    const quiz = steps.quiz.find((q) => q.word === p.word);
    const cloze = steps.cloze.find((c) => c.word === p.word);
    const say = steps.say.find((x) => x.word === p.word);
    const wanted = reviewKind(p.box, { picture: quiz !== undefined, say: say !== undefined });
    // gdy lekcja nie ma ćwiczenia wybranego rodzaju, bierzemy najbliższe, które ma (każdy cel ma pytanie i lukę, ale nie każdy kartę „Co powiesz?”)
    const kind = wanted === 'say' && say ? 'say' : wanted === 'cloze' && cloze ? 'cloze' : (wanted === 'picture' || wanted === 'choose') && quiz ? 'quiz' : cloze ? 'cloze' : quiz ? 'quiz' : null;
    if (kind) items.push({ word: p.word, lesson, kind, ...(quiz ? { quiz } : {}), ...(cloze ? { cloze } : {}), ...(say ? { say } : {}) });
  }
  const tasks: Task[] = shuffleWith(items, rng).map((it, i): Task => {
    const id = `${String(i + 1).padStart(2, '0')}:review:${it.kind}:${it.word}`;
    // własne ziarno na słowo: kolejność odpowiedzi nie zależy od tego, które inne słowa są w sesji
    const wordRng = mulberry32(hashSeed(`${input.seed}|${it.word}`));
    if (it.kind === 'quiz') return quizTaskOf(id, it.quiz!, wordRng);
    if (it.kind === 'cloze') return clozeTaskOf(id, it.cloze!, withRole(it.lesson, input.gender), true);
    return sayTaskOf(id, it.say!, level, seedNum);
  });
  return localizeCourseTasks(tasks, input.gender ?? '');
}

/** Polskie teksty zadań z formami zależnymi od płci rozwiązane dla dziecka (`szczęśliw{y|a}`) — widoki dostają gotowy tekst. */
export function localizeCourseTasks(tasks: readonly Task[], gender: Gender): Task[] {
  const fg = (t: string): string => forGender(t, gender);
  return tasks.map((t): Task => {
    switch (t.kind) {
      case 'banter':
        return t.line.pl ? { ...t, line: { ...t.line, pl: fg(t.line.pl) } } : t;
      case 'speak':
        return { ...t, pl: fg(t.pl), ...(t.cue ? { cue: t.cue.map((c) => ({ ...c, pl: fg(c.pl) })) } : {}) };
      case 'build':
        return { ...t, pl: fg(t.pl) };
      case 'cloze':
        return { ...t, word: { ...t.word, pl: fg(t.word.pl) }, pl: fg(t.pl), ...(t.cue ? { cue: { ...t.cue, pl: fg(t.cue.pl) } } : {}) };
      case 'quiz':
        return { ...t, pl: fg(t.pl), wordPl: fg(t.wordPl), ...(t.tile ? { tile: { ...t.tile, situationPl: fg(t.tile.situationPl) } } : {}) };
      case 'say':
        return { ...t, situationPl: fg(t.situationPl), partner: { ...t.partner, pl: fg(t.partner.pl) }, cards: t.cards.map((c) => ({ ...c, pl: fg(c.pl), ...(c.whyPl ? { whyPl: fg(c.whyPl) } : {}) })) };
      case 'grammar':
        return t;
    }
  });
}
